import React, { useMemo } from 'react';

import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

import { Button, Container, Stack } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { useListAudienceUsers } from '~/hooks/apis/promo/useListAudienceUsers';
import { useListAudiences } from '~/hooks/apis/promo/useListAudiences';

import AudiencesListTable from './AudiencesListTable';

export default function AudiencesPage() {
  const audienceQuery = useMemo(() => ({ page: 0, limit: 100 }), []);
  const audienceUsersQuery = useMemo(() => ({ enabled: 'all' as const, page: 0, pageSize: 100 }), []);

  const audiences = useListAudiences({ query: audienceQuery });
  const audienceUsers = useListAudienceUsers({ query: audienceUsersQuery });

  const audienceRows = audiences.data?.result?.audiences ?? [];
  const audienceUserRows = audienceUsers.data?.result?.audienceUsers ?? [];

  const audienceColumns = useMemo(
    () => [
      { key: 'id', label: 'ID' },
      { key: 'description', label: 'Description' },
      { key: 'created_at', label: 'Created at' },
      { key: 'updated_at', label: 'Updated at' },
    ],
    [],
  );

  const audienceUserColumns = useMemo(
    () => [
      { key: 'audience_id', label: 'Audience ID' },
      { key: 'user_id', label: 'User ID' },
      { key: 'email', label: 'Email' },
      { key: 'created_at', label: 'Created at' },
    ],
    [],
  );

  return (
    <>
      <Helmet>
        <title>Marketing / Audiences</title>
      </Helmet>
      <Container maxWidth="xl">
        <PageHeader title="Audiences">
          <Button component={Link} to="/marketing/audiences/create" variant="contained">
            Create audience
          </Button>
        </PageHeader>
        <Stack spacing={3}>
          <AudiencesListTable
            title="Audiences"
            description="All audiences exposed by svc-promo."
            rows={audienceRows}
            isLoading={audiences.isLoading}
            isError={audiences.isError}
            errorMessage={audiences.error instanceof Error ? audiences.error.message : 'Failed to load audiences'}
            emptyMessage="No audiences found."
            columns={audienceColumns}
          />
          <AudiencesListTable
            title="Audience Users"
            description="Users assigned to an audience."
            rows={audienceUserRows}
            isLoading={audienceUsers.isLoading}
            isError={audienceUsers.isError}
            errorMessage={
              audienceUsers.error instanceof Error ? audienceUsers.error.message : 'Failed to load audience users'
            }
            emptyMessage="No audience users found."
            columns={audienceUserColumns}
          />
        </Stack>
      </Container>
    </>
  );
}
